import React, { useState, useEffect, useMemo } from "react";
import { FLAVOR, icons } from "../helpers/constants";
import { useTranslation } from "react-i18next";
import Account from "../components/Settings/Account";
import Safety from "../components/Settings/Safety";
import Devices from "../components/Settings/Devices";
import Billing from "../components/Settings/Billing";
import Notifications from "../components/Settings/Notifications";
import Support from "../components/Settings/Support";
import Warranty from "../components/Settings/Warranty";
import Manuals from "../components/Settings/Manuals";
import ApiAccess from "../components/Settings/ApiAccess";
import LogoutModal from "../components/Modals/LogoutModal";
import Button from "../components/Buttons/Button";
import { useIsMobile } from "../helpers/customHooks";
import { useLocation, useNavigate } from "react-router-dom";
import { useSidebarContext } from "../components/Sidebar/SiderbarContext";
import { AnimatePresence, motion } from "framer-motion";
import NewButton from "../components/Buttons/NewButton";
import * as request from "../services/FetchService";
import MessageRow from "../components/Messages/MessageRow";
import { fixDate } from "../helpers/helperFunctions";
import OpenMessage from "../components/Messages/OpenMessage";
import OpenMessageNew from "../components/Messages/OpenMessageNew";
import toast from "react-hot-toast";

const Messages = ({}) => {
	const MOBILE = useIsMobile();
	const navigate = useNavigate();
	const [selected, setSelected] = useState(null);
	const { i18n, t } = useTranslation();
	const [showLogout, setShowLogout] = useState(false);
	const [isOpen, setIsOpen] = useState(false);
	const [ticketOpen, setTicketOpen] = useState(false);
	const [ticketId, setTicketId] = useState(null);
	const location = useLocation();
	const [messages, setMessages] = useState([]);
	const { setActionbarContent, topbarContent, setTopbarContent, topbarTwoContent, setTopbarTwoContent } = useSidebarContext();
	const [filteredType, setFilteredType] = useState("all");
	const [selectedMessage, setSelectedMessage] = useState(null);


	const filteredMessages = useMemo(() => {
		if (!messages) return;
		if (filteredType === "all") return messages;
		return messages.filter((m) => m.type === filteredType);
	}, [messages, filteredType]);

	const FILTERS = [
		{ label: t("inapp.all"), value: "all" },
		{ label: t("inapp.messages"), value: "message" },
		{ label: t("inapp.tickets"), value: "tickets" },
		{ label: t("inapp.alerts"), value: "alerts" },
	];

	useEffect(() => {
		getMessages();

			
		return() => {
			setTopbarTwoContent(null);
		}
		
	}, []);

	useEffect(() => {
		const onLanguageChanged = () => {
			setTimeout(() => {
				getMessages().then((inner) => {
					if (!selectedMessage) return;

					if (selectedMessage.type === "alerts" || selectedMessage.type === "share") {
						const updated = inner?.find((m) => m.messageid === selectedMessage.messageid);
						if (updated) setSelectedMessage(updated);
					} else {
						forceSpecific(selectedMessage.messageid);
					}
				});
			}, 500);
		};

		i18n.on("languageChanged", onLanguageChanged);
		return () => {
			i18n.off("languageChanged", onLanguageChanged);
		};
	}, [i18n, selectedMessage, messages]);

	const getMessages = () => {
		setMessages(undefined);
		return request.get("message/list").then((response) => {
			if (response.data.successful) {
				const data = response.data.messages.slice().reverse();
				setMessages(data);
				return data;
			}
			return undefined;
		});
	};

	const forceSpecific = (id) => {
		request.post("", "inappmessage/info", { messageid: id }).then((response) => {
			if (response.data.successful) {
				//setSharedMessage()
				setSelectedMessage(response.data.message);
				if (response.data.message.isread === 0) {
					getMessages();
				}
			}
		});
	};

	const getSpecificMessage = (id) => {
		if (selectedMessage?.messageid === id) {
			return;
		}
		request.post("", "inappmessage/info", { messageid: id }).then((response) => {
			if (response.data.successful) {
				//setSharedMessage()
				setSelectedMessage(response.data.message);
				if (response.data.message.isread === 0) {
					getMessages();
				}
			}
		});
	};

	const handleMessage = async (clickedmessage, index) => {
		if (clickedmessage.type === "tickets") {
			navigate("/settings", { state: { ticketid: clickedmessage?.ticket_id } });
			return;
		}
		if (clickedmessage.type === "message" || clickedmessage.type === "info" || clickedmessage.type === "standard") {
			await getSpecificMessage(clickedmessage.messageid);
			setMessages((prev) =>
				prev?.map((message) => (message.messageid === clickedmessage.messageid ? { ...message, isread: 1 } : message))
			);
		} else if (clickedmessage.type === "share" || clickedmessage.type === "alerts") {
			setSelectedMessage(clickedmessage);
		}
	};

	useEffect(() => {
		if (!selectedMessage) {
			setTopbarContent(null);
			setTopbarTwoContent(null);
			return;
		}

		const topTwoContent = {
			left: (
				<div className="d-flex align-items-center">
					<div className="cursor-pointer round-bg bg-white menu-button d-flex align-items-center justify-content-center" onClick={() => setSelectedMessage(null)}>
						<motion.img key="close" src={icons.chevron_left} alt="close" className="size_15 primary-icon" initial={{ opacity: 1, rotate: 90 }} animate={{ opacity: 1, rotate: 0 }} exit={{ opacity: 1, rotate: 180 }} transition={{ duration: 0.2 }} />
					</div>
					<motion.h4 key="title" className="ms-3" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
						{`${selectedMessage?.title}`}
					</motion.h4>
				</div>
			),
		};

		setTopbarTwoContent({ ...topbarTwoContent, ...topTwoContent });

		if (MOBILE) {
			const topcontent = {
				left: (
					<div className="cursor-pointer round-bg bg-white menu-button d-flex align-items-center justify-content-center" onClick={() => setSelectedMessage(null)}>
						<motion.img key="close" src={icons.chevron_left} alt="close" className="size_15 primary-icon" initial={{ opacity: 1, rotate: 90 }} animate={{ opacity: 1, rotate: 0 }} exit={{ opacity: 1, rotate: 180 }} transition={{ duration: 0.2 }} />
					</div>
				),
				center: (
					<motion.h4 key="title" className="position-absolute top-50 start-50 translate-middle m-0 text-truncate w-50" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
						{`${selectedMessage?.title}`}
					</motion.h4>
				)
			};
			setTopbarContent({ ...topbarContent, ...topcontent });
		}
	}, [selectedMessage]);

	const messageList = () => {
		if (MOBILE && selectedMessage) return;
		return (
			<>
				<div className={`info-section-nomap section-divider position-relative scrollable-y`}>
					<div className="margin-x-20 d-flex flex-column">
						{/* <p className={`mb-1 bold mt-3`}>{t("settings.my_account")}</p> */}
						<div className="select-wrap ms-auto mb-2 mt-3">
							<select value={filteredType} onChange={(e) => setFilteredType(e.target.value)} className="bg-white form-control text-truncate height36 full-rounded">
								{FILTERS.map((opt, index) => {
									return (
										<option key={index} value={opt.value}>
											{opt.label}
										</option>
									);
								})}
							</select>
						</div>
						<div className="d-flex flex-column settings-radius">
							{filteredMessages?.map((message, index) => {
								const showDot = message.isread === 0 || message.type === "alerts"
								return (
									<div
										key={index}
										onClick={() => handleMessage(message)}
										className={`p-3 d-flex justify-content-start align-items-center cursor-pointer ${index < filteredMessages.length - 1 ? "border-settings" : ""} ${selectedMessage === message || (selectedMessage?.messageid && selectedMessage?.messageid === message?.messageid) ? "bg-grey-blue" : "bg-white"}`}
									>
										<div className={`d-flex justify-content-center align-items-center bg-grey-blue full-rounded size_36 me-3 position-relative`}>
											<img src={icons.envelope_gradient} className=" white-icon size_20" alt="" />
											{showDot && <span className="position-absolute dot bg-offline top-0 end-0 me-0" />}
										</div>

										<div className="d-flex flex-column flex-stretch overflow-hidden">
											<h4 className={`w-100 fast text-truncate ${selectedMessage === message || (selectedMessage?.messageid && selectedMessage?.messageid === message?.messageid) ? "white" : "grey-blue"}`}>{message.title}</h4>
											<p className={`${selectedMessage === message || (selectedMessage?.messageid && selectedMessage?.messageid === message?.messageid) ? "white" : "grey-blue"}`}>{fixDate(message.creationdate, true)}</p>
										</div>
									</div>
								);
								// return(
								//     <MessageRow
								//         key={index}
								//         message={message}
								//         //openMessage={selectedMessage?.messageid}
								//         //sharedMessage={sharedMessage}
								//         index={index}
								//         onClick={(clickedmessage) => handleMessage(clickedmessage)} />
								// )
							})}
							{filteredMessages?.length === 0 && (
								<div className="d-flex justify-content-center align-items-center h-100">
                                	<h3 className="dark_grey">{filteredType === "all" ? t("inapp.no_messages") : t(`inapp.no_${filteredType}`)}</h3>
								</div>
							)}
						</div>
					</div>
				</div>
			</>
		);
	};

	const handleInvite = (accept, id) => {
		request.post("", "device/share/acceptdenyinvitation", { deviceid: [id], accept: accept }).then((response) => {
			if (response.data.successful) {
				toast.success(t("toast.success"))
				setTimeout(() => {
					setSelectedMessage(null)
					getMessages()	
				}, 500);

			} else {
				const msg = response?.data?.message
                toast.error(msg ?? t("toast.error"))
			}
		});
	};

	const selectedMessageView = () => {
		if (!selectedMessage) return;

		if (selectedMessage.type === "share" || selectedMessage.type === "alerts") {
			return (
				<div className="bg-white rounded24 p-3">
					<img className="w-100 mx-auto modelphoto mb-5" src={selectedMessage.image} alt="" />
					<p>{selectedMessage ? selectedMessage?.content : t("inapp.share_device_body", { from: selectedMessage.sharedfrom, device: selectedMessage.devicename })}</p>
					<div className="d-flex flex-row justify-content-between align-items-center mt-4">
						<NewButton onClick={() => handleInvite(false, selectedMessage.deviceid)} className={`bg-red rounded24 w-100 white p-3 me-2 body bold`}>
							{t("inapp.decline")}
						</NewButton>
						<NewButton onClick={() => handleInvite(true, selectedMessage.deviceid)} className="bg-gradient rounded24 w-100 white ms-2 p-3 body bold">
							{t("inapp.accept")}
						</NewButton>
					</div>
				</div>
			);
		}

		return (
			<div className="w-100 bg-white rounded24 p-3">
				{/* <div className="d-flex flex-row justify-content-between align-items-center">
						<div className='d-flex align-items-center'>
							<h3 className="">{m.title}</h3>
						</div>
					</div> */}
				<OpenMessageNew message={selectedMessage} />
			</div>
		);
	};

	return (
		<>
			{messageList()}
			<div className={`settings-right-section scrollable-y`}>{selectedMessageView()}</div>

			<LogoutModal show={showLogout} onClose={() => setShowLogout(!showLogout)} />
		</>
	);
};

export default Messages;
